import * as Sentry from "@sentry/nextjs";
import { toJS } from "mobx";

import { ComputedReferenceNodeModel } from "@/models/nodes/ComputedReferenceNodeModel";
import { ComputedResultNodeModel } from "@/models/nodes/ComputedResultNodeModel";
import { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import { DecoratorNodeModel } from "@/models/nodes/DecoratorNodeModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { GraphExpressionNodeModel } from "@/models/nodes/GraphExpressionNodeModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { LabelNodeModel } from "@/models/nodes/LabelNodeModel";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import { PlaceholderNodeModel } from "@/models/nodes/PlaceholderNodeModel";
import { PlainTextNodeModel } from "@/models/nodes/PlainTextNodeModel";
import { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";
import { TextNodeModel } from "@/models/nodes/TextNodeModel";
import { UnitNodeModel } from "@/models/nodes/UnitNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import { GraphNodeModel } from "./GraphNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel, SerializedNode } from "@/models/nodes/types";
import type { NodeJson } from "@corca/data";

// Using overload for NodeModel. When we pass NodeModel, it will always
// return NodeModel, and it can't return null.
export function createNodeFrom<T extends NodeModel>(
  node: T,
  doc: DocumentModel,
  options?: { resetIds?: boolean; skipRootIdReset?: boolean },
): T;
export function createNodeFrom(
  node: SerializedNode | NodeJson,
  doc: DocumentModel,
  options?: { resetIds?: boolean; skipRootIdReset?: boolean },
): NodeModel | null;

export function createNodeFrom(
  node: SerializedNode | NodeJson | NodeModel,
  doc: DocumentModel,
  options?: { resetIds?: boolean; skipRootIdReset?: boolean; noRegister?: boolean },
): NodeModel | null | undefined {
  return doc.batchCreateNodes(() => {
    const { skipRootIdReset = false, resetIds = false } = options ?? {};
    const id = resetIds && !skipRootIdReset ? undefined : node.id;

    let childrenIds;

    if ("childrenPlease" in node || "children" in node) {
      childrenIds = ("childrenPlease" in node ? node.childrenPlease : node.children)
        .map((childJson) => {
          const childNode = createNodeFrom(childJson, doc, { resetIds });
          return childNode?.id;
        })
        .filter(Boolean);
    } else {
      childrenIds = node.childrenIds;
    }

    switch (node.type) {
      case GroupNodeModel.type: {
        return new GroupNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case NumberNodeModel.type: {
        return new NumberNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case OperatorNodeModel.type: {
        if (!OperatorNodeModel.validateJson(node.props)) {
          return null;
        }
        return new OperatorNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case OperatorSymbolNodeModel.type: {
        return new OperatorSymbolNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case VariableNodeModel.type: {
        return new VariableNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case LabelNodeModel.type: {
        return new LabelNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case SymbolNodeModel.type: {
        return new SymbolNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case TextNodeModel.type: {
        return new TextNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case WrapperNodeModel.type: {
        return new WrapperNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case ConstantNodeModel.type: {
        return new ConstantNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case FunctionNodeModel.type: {
        return new FunctionNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case InlineNodeModel.type: {
        return new InlineNodeModel(doc, id, {}, childrenIds);
      }

      case PlainTextNodeModel.type: {
        return new PlainTextNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case MatrixNodeModel.type: {
        return new MatrixNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case DecoratorNodeModel.type: {
        return new DecoratorNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case GraphNodeModel.type: {
        return new GraphNodeModel(doc, id, {}, childrenIds);
      }

      case GraphExpressionNodeModel.type: {
        return new GraphExpressionNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case UnitNodeModel.type: {
        return new UnitNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case PlaceholderNodeModel.type: {
        return new PlaceholderNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case ComputedReferenceNodeModel.type: {
        return new ComputedReferenceNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      case ComputedResultNodeModel.type: {
        return new ComputedResultNodeModel(doc, id, toJS(node.props), childrenIds);
      }

      default: {
        Sentry.captureMessage(`Unknown node type ${node.type}`, "error");
        console.warn(`Unknown node type ${node.type}`);
        return null;
      }
    }
  });
}
